var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _NiceRelay_instances, _NiceRelay_relay, _NiceRelay_pubsub, _NiceRelay_controller, _NiceRelay_reqlog, _NiceRelay_reanimate, _NiceRelay_init, _NiceRelay_reqsync;
import { AsyncSocket } from './async-socket.js';
import { matchFilters as _matchFilters } from './deps.js';
import { Pubsub } from './pubsub.js';
import { relayMsgSchema, relayOkSchema } from './schema.js';
import { reanimate } from './reanimate.js';
import { Relay } from './relay.js';
/** Opinionated relay implementation that manages subscriptions and auto-reconnects. */
class NiceRelay {
    constructor(url, opts = {}) {
        _NiceRelay_instances.add(this);
        /** Internal WebSocket for the relay connection. */
        Object.defineProperty(this, "socket", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        _NiceRelay_relay.set(this, void 0);
        _NiceRelay_pubsub.set(this, new Pubsub());
        _NiceRelay_controller.set(this, new AbortController());
        _NiceRelay_reqlog.set(this, new Map());
        this.socket = new WebSocket(url);
        __classPrivateFieldSet(this, _NiceRelay_relay, new Relay(this.socket), "f");
        __classPrivateFieldGet(this, _NiceRelay_instances, "m", _NiceRelay_reanimate).call(this, opts);
        __classPrivateFieldGet(this, _NiceRelay_instances, "m", _NiceRelay_init).call(this);
    }
    /**
     * Subscribe to events from the relay.
     *
     * @example
     * ```ts
     * const sub = relay.req([
     *   { kinds: [1], authors: ['79c2...'] },
     * ]);
     *
     * for await (const event of sub) {
     *   handleEvent(event);
     * }
     * ```
     */
    req(filters, opts = {}) {
        const { id = crypto.randomUUID(), matchFilters = _matchFilters } = opts;
        let eoseController = new AbortController();
        const sub = __classPrivateFieldGet(this, _NiceRelay_pubsub, "f").subscribe(id);
        const msg = ['REQ', id, ...filters];
        __classPrivateFieldGet(this, _NiceRelay_reqlog, "f").set(id, msg);
        this.send(msg);
        return {
            id,
            close: () => {
                __classPrivateFieldGet(this, _NiceRelay_reqlog, "f").delete(id);
                this.send(['CLOSE', id]).catch(() => { });
                eoseController = new AbortController();
                sub.close();
            },
            get eoseSignal() {
                return eoseController.signal;
            },
            [Symbol.asyncIterator]: async function* () {
                for await (const msg of sub.stream()) {
                    switch (msg[0]) {
                        case 'EVENT':
                            if (matchFilters(filters, msg[2])) {
                                yield msg[2];
                            }
                            break;
                        case 'EOSE':
                            eoseController.abort();
                            break;
                    }
                }
            },
        };
    }
    async send(msg, opts = {}) {
        const { timeout = 1000 } = opts;
        const waitForSend = __classPrivateFieldGet(this, _NiceRelay_relay, "f").send(msg);
        if (msg[0] === 'EVENT' && isEntity(msg[1]) && timeout) {
            const event = msg[1];
            const controller = new AbortController();
            const tid = setTimeout(() => controller.abort(), timeout);
            const messages = __classPrivateFieldGet(this, _NiceRelay_relay, "f").stream(controller.signal);
            await waitForSend;
            for await (const resp of messages) {
                if (resp[0] === 'OK' && resp[1] === event.id) {
                    const result = relayOkSchema.safeParse(resp);
                    if (result.success) {
                        cleanup();
                        return result.data;
                    }
                }
            }
            function cleanup() {
                clearTimeout(tid);
                controller.abort();
            }
        }
        await waitForSend;
    }
    /** Close the relay connection. Resolves when the WebSocket is fully closed. */
    async close() {
        __classPrivateFieldGet(this, _NiceRelay_controller, "f").abort();
        __classPrivateFieldGet(this, _NiceRelay_pubsub, "f").close();
        await __classPrivateFieldGet(this, _NiceRelay_relay, "f").close();
    }
}
_NiceRelay_relay = new WeakMap(), _NiceRelay_pubsub = new WeakMap(), _NiceRelay_controller = new WeakMap(), _NiceRelay_reqlog = new WeakMap(), _NiceRelay_instances = new WeakSet(), _NiceRelay_reanimate = 
/** Rig this socket up for reanimation, and re-setup state. */
async function _NiceRelay_reanimate(opts) {
    for await (const socket of reanimate(this.socket, opts)) {
        this.socket = socket;
        __classPrivateFieldSet(this, _NiceRelay_relay, new Relay(socket), "f");
        __classPrivateFieldGet(this, _NiceRelay_instances, "m", _NiceRelay_init).call(this);
        __classPrivateFieldGet(this, _NiceRelay_instances, "m", _NiceRelay_reqsync).call(this);
    }
}, _NiceRelay_init = 
/** Watch Nostr messages and distribute them to subscribers. */
async function _NiceRelay_init() {
    for await (const msg of __classPrivateFieldGet(this, _NiceRelay_relay, "f").stream(__classPrivateFieldGet(this, _NiceRelay_controller, "f").signal)) {
        const result = relayMsgSchema.safeParse(msg);
        if (!result.success)
            continue;
        switch (result.data[0]) {
            case 'EVENT':
            case 'EOSE':
                __classPrivateFieldGet(this, _NiceRelay_pubsub, "f").publish(result.data[1], result.data);
                break;
        }
    }
}, _NiceRelay_reqsync = 
/** Resend `REQ` messages. */
async function _NiceRelay_reqsync() {
    await AsyncSocket.waitForOpen(this.socket);
    const reqs = [...__classPrivateFieldGet(this, _NiceRelay_reqlog, "f").values()];
    const sendPromises = reqs.map((req) => __classPrivateFieldGet(this, _NiceRelay_relay, "f").send(req));
    return Promise.all(sendPromises);
};
/** Whether the value is an object with an `id` property. */
function isEntity(entity) {
    return !!entity && typeof entity === 'object' && 'id' in entity && typeof entity.id === 'string';
}
export { NiceRelay };
